import React from "react";
import { canonical } from "../data/business";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import FaqAccordion from "../components/FaqAccordion";
import ArtificialGrassCalculator from "../components/ArtificialGrassCalculator";
import GrassOfferBanner from "../components/GrassOfferBanner";
import { findService } from "../data/services";
import { locations } from "../data/locations";
import { IMG } from "../data/images";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = findService(slug);
  if (!s) return <Navigate to="/services" replace />;

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Service", "name": s.name, "provider": { "@type": "LocalBusiness", "name": "Last Cut Landscaping" }, "areaServed": "Central Belt of Scotland", "description": s.intro },
    { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": s.faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  ];

  return (
    <div className="page-enter">
      <Seo title={`${s.name} Scotland — Premium Installs | Last Cut Landscaping`} description={`${s.short} Free quotes across Edinburgh, Glasgow, Falkirk & the Central Belt of Scotland.`} canonical={canonical(`/services/${s.slug}`)} jsonLd={jsonLd} />

      <section className="bg-cream pt-8 lg:pt-12 pb-12 lg:pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs items={[{ label: "Services", to: "/services" }, { label: s.name }]} />
          <div className="grid lg:grid-cols-12 gap-10 mt-6 items-end">
            <div className="lg:col-span-7">
              <div className="pill mb-4">Service</div>
              <h1 className="text-display font-display text-[clamp(2.4rem,5vw,4.5rem)] text-dark">{s.name} <span className="italic text-[color:var(--lc-green)]">in Scotland</span></h1>
              <p className="text-lg text-dark/75 mt-5 max-w-2xl">{s.intro}</p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/quote" className="btn-primary">Get my free quote <ArrowRight size={16}/></Link>
                <a href="#process" className="btn-ghost">How we install</a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                <img
                  src={s.image}
                  alt={s.slug === "artificial-grass"
                    ? "Artificial grass installation by Last Cut Landscaping in Scotland"
                    : `${s.name} install by Last Cut Landscaping`}
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  width="1200"
                  height="900"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {s.slug === "artificial-grass" && <GrassOfferBanner />}

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Why choose us</div>
            <h2 className="font-display text-3xl lg:text-4xl text-dark mb-6">Benefits of our {s.name.toLowerCase()}</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {s.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-paper rounded-xl"><Check className="text-[color:var(--lc-green)] mt-0.5" size={18}/><span className="text-sm text-dark/80">{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <QuoteForm defaultService={s.name} sourcePage={`/services/${s.slug}`} />
          </div>
        </div>
      </section>

      {s.slug === "artificial-grass" && <ArtificialGrassCalculator />}

      {s.process && (
        <section id="process" className="py-16 lg:py-24 bg-paper">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="eyebrow mb-3">Our process</div>
            <h2 className="font-display text-3xl lg:text-5xl text-dark mb-10">How we install your {s.name.toLowerCase()}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {s.process.map((p, i) => (
                <div key={i} className="lc-card p-6">
                  <div className="font-display text-5xl text-[color:var(--lc-lime)]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-display text-xl text-dark mt-3">{p.step}</div>
                  <div className="text-dark/65 text-sm mt-2">{p.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3 text-center">FAQs</div>
          <h2 className="font-display text-3xl lg:text-5xl text-dark mb-8 text-center">{s.name} — your questions</h2>
          <FaqAccordion items={s.faqs} />
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Areas covered</div>
          <h2 className="font-display text-3xl lg:text-4xl text-dark mb-6">{s.name} across the Central Belt of Scotland</h2>
          <div className="flex flex-wrap gap-2">
            {locations.map((l) => (
              <Link key={l.slug} to={`/${s.slug}/${l.slug}`} className="px-4 py-2 rounded-full bg-paper border border-lc text-sm text-dark hover:bg-dark hover:text-white transition">{s.name} {l.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-dark text-white relative noise">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center relative">
          <h2 className="font-display text-3xl lg:text-5xl">Ready for premium {s.name.toLowerCase()}?</h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">Free, no-obligation quote within 24 hours.</p>
          <Link to="/quote" className="btn-accent mt-6">Get my free quote <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}
